import "react-toastify/dist/ReactToastify.css";

import { toast as toastify, ToastOptions } from "react-toastify";
import useApp from "./useApp";
import { useLanguage } from "./useLanguage";

type ToastMessage = {
    message: any;
    type?: "info" | "error" | "warning" | "success";
    properties?: ToastOptions | undefined;
    toastId?: string;
};

type ToastMessageHandlingPromise<T> = {
    resolve: Promise<T> | undefined;
    messagesPromise: {
        pending: string;
        success: string;
        error: string;
    };
    properties?: ToastOptions | undefined;
};

type ToastMessageUpdate = {
    toastId: string | number;
    properties?: ToastOptions | undefined;
    render: string;
    type?: "info" | "error" | "warning" | "success";
};

interface Toast {
    addToast(message: ToastMessage): void;
    addToastHandlingPromise<T>(promise: ToastMessageHandlingPromise<T>): void;
    removeToast(toastId: string): void;
    removeAllToast(): void;
    updateToast(properties: ToastMessageUpdate): void;
}

function useToast(): Toast {
    const { theme } = useApp();
    const { t } = useLanguage();

    const addToast = ({
        message,
        type = "info",
        properties,
        toastId,
    }: ToastMessage) => {
        toastify(t(message), {
            type,
            theme: theme.inverted ? "dark" : "light",
            toastId,
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            ...properties,
        });
    };

    const addToastHandlingPromise = <T>({
        resolve,
        messagesPromise,
        properties,
    }: ToastMessageHandlingPromise<T>) => {
        toastify.promise(
            resolve || new Promise<T>(() => ({})),
            {
                error: t(messagesPromise.error),
                success: t(messagesPromise.success || "Sucess") || "Sucess",
                pending: t(messagesPromise.pending),
            },
            {
                ...properties,
            }
        );
    };

    const removeToast = (toastId: string | number) => {
        toastify.dismiss(toastId);
    };

    const removeAllToast = () => {
        toastify.dismiss();
    };

    const updateToast = ({
        properties,
        toastId,
        render,
        type,
    }: ToastMessageUpdate) => {
        toastify.update(toastId, {
            ...properties,
            theme: theme.inverted ? "dark" : "light",
            render,
            type,
        });
    };

    return {
        addToast,
        addToastHandlingPromise,
        removeToast,
        removeAllToast,
        updateToast,
    };
}

export default useToast;
